'use client'

import { useRouter } from 'next/navigation'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { getUser, updateUser } from '@/app/actions/user'
import { FormAccessRequired } from '@/components/FormAccessRequired'
import { FormMessage } from '@/components/FormMessage'
import RichText from '@/components/RichText'
import { Button } from '@/components/ui/button'
import { useScrollToRef } from '@/hooks/useScrollToRef'
import { cn } from '@/utilities/ui'

import { fields } from './fields'

import type { User } from '@/payload-types'
import type { FormFieldBlock, Form as FormType } from '@payloadcms/plugin-form-builder/types'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

// Extended form type to support sections
export type FormSection = {
  id?: string
  sectionHeading?: string | null
  sectionSubheading?: DefaultTypedEditorState | null
  fields?: FormFieldBlock[]
  border?: boolean | null
}

export type ExtendedFormType = FormType & {
  isFormGated?: boolean | null
  heading?: string | null
  subheading?: DefaultTypedEditorState | null
  sections?: FormSection[]
}

type FormBlockType = {
  blockName?: string
  blockType?: 'formBlock'
  enableIntro: boolean
  form: ExtendedFormType
  introContent?: DefaultTypedEditorState
  formDrawer?: boolean
}

export const FormBlock: React.FC<
  {
    id?: string
    className?: string
  } & FormBlockType
> = (props) => {
  const {
    enableIntro,
    form: formFromProps,
    form: {
      id: formID,
      confirmationMessage,
      confirmationType,
      redirect,
      submitButtonLabel,
      heading,
      subheading,
    } = {},
    introContent,
    formDrawer,
    className,
  } = props
  const [isUpdating, setIsUpdating] = useState(false)
  const [payloadUser, setPayloadUser] = useState<User | null>(null)
  const formContainerRef = useRef<HTMLDivElement>(null)
  const scrollToRef = useScrollToRef()

  // When user is logged in, fetch Payload user so we can pre-populate and run updateUser
  useEffect(() => {
    const run = async () => {
      const data = await getUser()
      if (data.status === 'error') {
        setPayloadUser(null)
        return
      } else {
        setPayloadUser(data.data)
      }
    }
    run()
  }, [])

  const defaultValues = useMemo(() => {
    const collect = (formFields: FormFieldBlock[] | undefined): Record<string, unknown> => {
      const out: Record<string, unknown> = {}
      if (!formFields) return out
      formFields.forEach((f) => {
        const block = f as { blockType?: string; name?: string; defaultValue?: unknown }
        if (typeof block.name !== 'string') return
        // Upload blocks should have no default (null); empty string would show as "Uploaded: file" when name is "file"
        if (block.blockType === 'uploadBlock') {
          out[block.name] = block.defaultValue ?? null
        } else if (block.blockType === 'checkbox') {
          out[block.name] = block.defaultValue === true
        } else {
          out[block.name] = block.defaultValue ?? ''
        }
      })
      return out
    }
    let base: Record<string, unknown>
    if (formFromProps?.sections?.length) {
      base = {}
      formFromProps.sections.forEach((s) => {
        Object.assign(base, collect(s.fields))
      })
    } else {
      base = collect(formFromProps?.fields)
    }
    const source = payloadUser
    if (!source) return base
    const fromUser: Record<string, unknown> = {}
    if ('firstName' in source && source.firstName != null) fromUser.first_name = source.firstName
    if ('lastName' in source && source.lastName != null) fromUser.last_name = source.lastName
    if (source.email != null) fromUser.email = source.email
    if (payloadUser?.profile?.mobile != null) fromUser.phone = payloadUser.profile.mobile
    if (payloadUser?.location?.location) {
      const loc = payloadUser.location.location
      if (loc.address != null) fromUser.address = loc.address
      if (loc.address2 != null) fromUser.address2 = loc.address2
      if (loc.city != null) fromUser.city = loc.city
      if (loc.state != null) fromUser.state = loc.state
      if (loc.zip != null) fromUser.zip_code = loc.zip
    }
    return { ...base, ...fromUser }
  }, [formFromProps, payloadUser])

  const formMethods = useForm<Record<string, unknown>>({
    defaultValues,
  })
  const {
    control,
    formState: { errors },
    watch,
    handleSubmit,
    register,
    reset,
  } = formMethods

  // Re-apply default values when user data loads (useForm only uses defaultValues on mount)
  useEffect(() => {
    const source = payloadUser
    if (source) {
      reset(defaultValues)
    }
  }, [payloadUser, defaultValues, reset])

  // Map: field name (conditional target) -> checkbox or radio field name that controls it.
  // When a checkbox has conditionalField set, the target field is shown only when that checkbox is checked.
  // When a radio button has conditionalFieldRules set, the target field is shown only when the selected option value matches the given option value.
  type ConditionalTargetMeta =
    | { kind: 'checkbox'; controllerName: string }
    | { kind: 'radioBlock'; controllerName: string; whenValues: string[] }

  const conditionalFieldToController = React.useMemo(() => {
    const map: Record<string, ConditionalTargetMeta> = {}
    const collect = (formFields: FormFieldBlock[] | undefined) => {
      if (!formFields) return
      formFields.forEach((f) => {
        const block = f as {
          blockType?: string
          name?: string
          conditionalField?: string
          conditionalFieldValue?: string | null
          conditionalFieldRules?:
            | { optionValue?: string | null; fieldName?: string | null }[]
            | null
        }
        const isCheckbox = block.blockType === 'checkbox'
        const isRadio = block.blockType === 'radioBlock'
        if (!isCheckbox && !isRadio) return
        if (typeof block.name !== 'string') return

        if (isCheckbox) {
          if (typeof block.conditionalField === 'string' && block.conditionalField.trim() !== '') {
            map[block.conditionalField.trim()] = {
              kind: 'checkbox',
              controllerName: block.name,
            }
          }
          return
        }

        const rules = block.conditionalFieldRules
        if (Array.isArray(rules) && rules.length > 0) {
          for (const row of rules) {
            const fieldName = typeof row?.fieldName === 'string' ? row.fieldName.trim() : ''
            const optionValue = typeof row?.optionValue === 'string' ? row.optionValue.trim() : ''
            if (!fieldName || !optionValue) continue

            const existing = map[fieldName]
            if (existing?.kind === 'radioBlock' && existing.controllerName === block.name) {
              if (!existing.whenValues.includes(optionValue)) {
                existing.whenValues.push(optionValue)
              }
            } else {
              map[fieldName] = {
                kind: 'radioBlock',
                controllerName: block.name,
                whenValues: [optionValue],
              }
            }
          }
          return
        }

        if (typeof block.conditionalField === 'string' && block.conditionalField.trim() !== '') {
          const target = block.conditionalField.trim()
          const raw = block.conditionalFieldValue
          const trimmed = typeof raw === 'string' ? raw.trim() : ''
          map[target] = {
            kind: 'radioBlock',
            controllerName: block.name,
            whenValues: trimmed === '' ? [] : [trimmed],
          }
        }
      })
    }
    if (formFromProps?.sections?.length) {
      formFromProps.sections.forEach((s) => collect(s.fields))
    } else {
      collect(formFromProps?.fields)
    }
    return map
  }, [formFromProps])

  // eslint-disable-next-line react-hooks/incompatible-library
  const formValues = watch()
  const conditionalTargetByName = React.useMemo(() => {
    const o: Record<string, boolean> = {}
    for (const [targetName, rule] of Object.entries(conditionalFieldToController)) {
      const v = formValues?.[rule.controllerName]
      if (rule.kind === 'checkbox') {
        o[targetName] = v === true
      } else {
        const selected = typeof v === 'string' ? v : ''
        o[targetName] =
          rule.whenValues.length === 0 ? selected.length > 0 : rule.whenValues.includes(selected)
      }
    }
    return o
  }, [conditionalFieldToController, formValues])

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  // const location = usePathname()

  const handleMediaUpload = useCallback(async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('path', 'media')
    formData.append('relationTo', 'media')
    const res = await fetch('/api/media', {
      method: 'POST',
      body: formData,
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.errors?.[0]?.message ?? 'Upload failed')
    // Payload create returns { doc, message }; we need the created document (with id)
    const doc = data.doc ?? data
    return doc as { id: number }
  }, [])

  // Collect form field names that are upload blocks (so we can send submissionMedia)
  const uploadFieldNames = React.useMemo(() => {
    const names: string[] = []
    const collect = (formFields: FormFieldBlock[] | undefined) => {
      if (!formFields) return
      formFields.forEach((f) => {
        const block = f as { blockType?: string; name?: string }
        if (block.blockType === 'uploadBlock' && typeof block.name === 'string') {
          names.push(block.name)
        }
      })
    }
    if (formFromProps?.sections?.length) {
      formFromProps.sections.forEach((s) => collect(s.fields))
    } else {
      collect(formFromProps?.fields)
    }
    return names
  }, [formFromProps])

  const onSubmit = useCallback(
    (data: Record<string, unknown>) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      const submitForm = async () => {
        setError(undefined)

        // Upload any File values for upload fields first (upload-on-submit flow)
        const uploadedIds: Record<string, number> = {}
        for (const name of uploadFieldNames) {
          const value = data[name]
          if (value instanceof File) {
            try {
              const doc = await handleMediaUpload(value)
              uploadedIds[name] = doc.id
            } catch (err) {
              console.error('Upload failed:', err)
              setError({ message: 'File upload failed. Please try again.' })
              return
            }
          }
        }

        const dataForSubmit = { ...data }
        uploadFieldNames.forEach((name) => {
          if (uploadedIds[name] != null) dataForSubmit[name] = uploadedIds[name]
        })

        //* User profile update for WorkOS and Payload. Doing this here before the form values are submitted to the backend/cleared
        const hasUserProfileFields =
          payloadUser?.id && ('first_name' in dataForSubmit || 'last_name' in dataForSubmit)
        if (hasUserProfileFields) {
          try {
            setIsUpdating(true)
            const res = await updateUser({
              ...payloadUser,
              ...dataForSubmit,
            })
            if (res.status === 'error') {
              setError({
                message: res.message,
              })
              return
            }
            setIsUpdating(false)
          } catch (error) {
            setIsUpdating(false)
            setError({
              message: error instanceof Error ? error.message : 'Failed to update profile',
            })
            return
          }
        }

        const dataToSend = Object.entries(dataForSubmit).map(([name, value]) => ({
          field: name,
          value: value != null ? String(value) : '',
        }))

        const submissionMedia: { field: string; media: number }[] = []
        uploadFieldNames.forEach((name) => {
          const value = dataForSubmit[name]
          if (value != null && value !== '') {
            const id = typeof value === 'number' ? value : Number(value)
            if (!Number.isNaN(id)) submissionMedia.push({ field: name, media: id })
          }
        })

        // Topics from Topics block: find field whose value is number[] and send as relationship array
        const submissionTopics = (() => {
          const entry = Object.entries(dataForSubmit).find(
            ([, v]) =>
              Array.isArray(v) && v.every((n) => typeof n === 'number' && Number.isInteger(n)),
          ) as [string, number[]] | undefined
          if (!entry || entry[1].length === 0) return []
          const [fieldName, topicIds] = entry
          return [{ field: fieldName, topics: topicIds }]
        })()

        // delay loading indicator by 1s
        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const req = await fetch('/api/form-submissions', {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
              ...(submissionMedia.length > 0 && { submissionMedia }),
              ...(submissionTopics.length > 0 && { submissionTopics }),
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })

          let res: { errors?: { message?: string }[] }
          try {
            res = await req.json()
          } catch {
            res = {}
          }

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)

            setError({
              message: res?.errors?.[0]?.message || 'Submission failed. Please try again.',
              status: String(req.status),
            })

            return
          }

          setIsLoading(false)
          setHasSubmitted(true)
          scrollToRef(formContainerRef)
          // Reset form so checkboxes and all fields clear if form is shown again (e.g. message confirmation)
          reset(defaultValues)

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect

            const redirectUrl = url

            if (redirectUrl) router.push(redirectUrl)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
        }
      }

      void submitForm()
    },
    [
      router,
      formID,
      redirect,
      confirmationType,
      uploadFieldNames,
      handleMediaUpload,
      payloadUser,
      defaultValues,
      reset,
      scrollToRef,
    ],
  )

  // Helper to render form fields. Conditional targets stay mounted and are hidden via CSS until their controller is "active".
  const renderFields = (formFields: FormFieldBlock[] | undefined) => {
    if (!formFields) return null

    return formFields.map((field, index) => {
      const block = field as FormFieldBlock & { name?: string; width?: number }
      const fieldName = block.name
      const isConditionalTarget = Boolean(fieldName && conditionalFieldToController[fieldName])
      const controllerName = fieldName
        ? conditionalFieldToController[fieldName]?.controllerName
        : undefined
      const controllerIsVisible =
        !controllerName ||
        !conditionalFieldToController[controllerName] ||
        conditionalTargetByName[controllerName]
      const shouldShow =
        !fieldName ||
        !isConditionalTarget ||
        (controllerIsVisible && conditionalTargetByName[fieldName])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]

      const setAsRequired = Boolean((field as FormFieldBlock & { required?: boolean }).required)
      const required = shouldShow && setAsRequired

      if (Field) {
        const isEmailField = fieldName === 'email'
        const disableEmail = isEmailField && payloadUser != null
        return (
          <div
            className={cn(
              block.width === 100 ? 'md:col-span-2' : 'col-span-1',
              !shouldShow && 'hidden',
            )}
            key={index}
          >
            <Field
              form={formFromProps}
              {...field}
              required={required}
              {...formMethods}
              control={control}
              errors={errors}
              register={register}
              onUpload={handleMediaUpload}
              uploadOnSubmit
              {...(disableEmail && { disabled: true })}
            />
          </div>
        )
      }
      return null
    })
  }

  // Check if form uses sections or legacy flat fields
  const hasSections = formFromProps?.sections && formFromProps.sections.length > 0

  if (formFromProps?.isFormGated && !payloadUser) {
    return (
      <div className="mb-8 border-b border-border pb-4">
        {heading && (
          <h2 className="mb-4 font-family-body text-3xl leading-9 font-semibold text-foreground">
            {heading}
          </h2>
        )}
        {subheading && (
          <RichText
            data={subheading}
            enableGutter={false}
            className="text-base leading-6 text-foreground"
          />
        )}
        <FormAccessRequired />
      </div>
    )
  }

  return (
    <div className="-mt-12">
      {enableIntro && introContent && !hasSubmitted && (
        <RichText className="mb-8 lg:mb-12" data={introContent} enableGutter={false} />
      )}
      <div
        ref={formContainerRef}
        className={cn(
          'rounded-2xl lg:px-14 lg:py-12',
          !formDrawer ? 'bg-background p-4 shadow-sm md:p-8' : 'p-0',
          className,
        )}
      >
        <div className="mb-8 border-b border-border pb-4">
          {heading && (
            <h2 className="mb-4 font-family-body text-3xl leading-9 font-semibold text-foreground">
              {heading}
            </h2>
          )}
          {subheading && (
            <RichText
              data={subheading}
              enableGutter={false}
              className="text-base leading-6 text-foreground"
            />
          )}
          {payloadUser && (
            <span className="mt-2 block text-xs text-muted-foreground">
              * Any changes to your information will update your MAHA user details.
            </span>
          )}
        </div>
        <FormProvider {...formMethods}>
          {!isLoading && hasSubmitted && confirmationType === 'message' && (
            // <RichText data={confirmationMessage} />
            <FormMessage type="success" message={confirmationMessage} />
          )}
          {isLoading && !hasSubmitted && <p>Loading, please wait...</p>}
          {error && <FormMessage type="error" message={error.message} className="mb-4" />}
          {!hasSubmitted && (
            <form id={formID} onSubmit={handleSubmit(onSubmit)}>
              {hasSections ? (
                // Render sections with headings and subheadings
                <div className="space-y-8">
                  {formFromProps.sections?.map((section, sectionIndex) => (
                    <div
                      key={section.id || sectionIndex}
                      className={cn(
                        'space-y-4',
                        section.border ? 'border-b border-border pb-4' : '',
                      )}
                    >
                      {section.sectionHeading && (
                        <h3 className="text-xl font-semibold">{section.sectionHeading}</h3>
                      )}
                      {section.sectionSubheading && (
                        <RichText
                          data={section.sectionSubheading}
                          enableGutter={false}
                          className="text-muted-foreground"
                        />
                      )}
                      <div className="mb-4 grid grid-cols-1 gap-4 last:mb-0 md:grid-cols-2 lg:gap-y-8">
                        {renderFields(section.fields)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Legacy: render flat fields structure
                <div className="mb-4 last:mb-0">{renderFields(formFromProps?.fields)}</div>
              )}
              <Button
                type="submit"
                className="mt-3 w-full"
                form={formID}
                disabled={isLoading || isUpdating}
              >
                {isLoading || isUpdating ? 'Submitting...' : submitButtonLabel}
              </Button>
            </form>
          )}
        </FormProvider>
      </div>
    </div>
  )
}
